/**
 * services/api.js — typed wrapper around inbox-proxy and inbox-auth Netlify functions.
 *
 * All API calls go through here. Components never call fetch() directly.
 * Errors throw with `{ status, message }` shape for consistent handling.
 */

import { getJwt, clearSession } from '../stores/session.js';

const BASE_AUTH  = '/.netlify/functions/inbox-auth';
const BASE_PROXY = '/.netlify/functions/inbox-proxy';

class ApiError extends Error {
  constructor(status, message, extra = {}) {
    super(message);
    this.status = status;
    // Attach any extra fields the server returned alongside the error
    // (e.g. retry_after_sec on 429). Components that care can read them
    // directly off the thrown error.
    Object.assign(this, extra);
  }
}

async function _request(url, options = {}) {
  const res = await fetch(url, options);
  const text = await res.text();
  let data;
  try { data = JSON.parse(text); } catch { data = { raw: text }; }

  if (!res.ok) {
    // Auto-logout on 401 (revoked session)
    if (res.status === 401) {
      clearSession();
    }
    // Forward all extra fields from the error body so callers can use
    // structured data (retry_after_sec, window_min, etc) without
    // string-parsing the message.
    const { error: _err, ...extra } = (data && typeof data === 'object') ? data : {};
    throw new ApiError(res.status, data.error || `Request failed (${res.status})`, extra);
  }
  return data;
}

/**
 * Build Authorization headers for authenticated requests.
 *
 * #108: this is the single place that decides whether to attach an
 * Authorization header. When the in-memory JWT exists (normal case,
 * after login or a successful boot rehydrate), we attach it. When the
 * JWT is null (e.g., immediately after a fresh page load, before
 * refreshSession() has run), we return an empty object and let the
 * server's extractInboxJwt helper fall back to the `inbox_jwt`
 * HttpOnly cookie. Both auth channels produce the same session.
 *
 * Previously every call site built `{ 'Authorization': 'Bearer ' +
 * getJwt() }` inline, which would send the literal string
 * "Bearer null" when the JWT was missing — guaranteed 401.
 */
function _authHeaders() {
  const jwt = getJwt();
  return jwt ? { 'Authorization': `Bearer ${jwt}` } : {};
}

// ── Auth ────────────────────────────────────────────────────────────────────

export async function sendOtp(identifier, method = 'phone') {
  return _request(`${BASE_AUTH}?action=send-otp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ identifier, method }),
  });
}

export async function verifyOtp(identifier, otp) {
  return _request(`${BASE_AUTH}?action=verify-otp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ identifier, otp }),
  });
}

export async function verifyMagicLink(token) {
  return _request(`${BASE_AUTH}?action=verify-magic`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });
}

export async function fetchMe() {
  return _request(`${BASE_AUTH}?action=me`, {
    headers: _authHeaders(),
  });
}

// Inbox 9.3 (2026-06-04) — manual refresh from Partner Panel. Hits PP
// directly so a channel added seconds ago appears immediately, not
// after the hourly sync cron tick.
export async function refreshChannels() {
  return _request(`${BASE_AUTH}?action=refresh-channels`, {
    method: 'POST',
    headers: _authHeaders(),
  });
}

/**
 * #108: refresh the session using whichever auth is available. Called
 * at boot when the in-memory JWT is null but the HttpOnly cookie may
 * still be valid. On success the server returns a fresh accessToken
 * in the body which the caller stores in $jwt. On failure (401) the
 * caller shows the login screen.
 *
 * 2026-04-17 — iOS PWA cold-launch retry. When the user kills the
 * installed PWA from the iOS task switcher and relaunches it, WKWebView
 * hydrates the HttpOnly cookie store lazily — the very first network
 * request can fire BEFORE inbox_jwt is attached, returning a transient
 * 401 even though the cookie is valid. The previous behavior (single
 * shot, then drop to Login on 401) made every iOS cold launch show
 * the login screen until the user closed + reopened a second time.
 *
 * Fix: retry once after a short delay on 401 to give the cookie store
 * a chance to catch up. Explicit credentials:'include' so WKWebView's
 * heuristic doesn't second-guess sending the cookie. Other failure
 * modes (network, 500) propagate immediately as before — this gate is
 * specifically for the cookie-not-yet-attached race.
 */
export async function refreshSession() {
  const opts = {
    method: 'POST',
    headers: _authHeaders(),
    credentials: 'include',
  };
  // #205 follow-up: Vite bundle loads in ~100ms (vs ~2-3s esm.sh waterfall),
  // so this fires much earlier on iOS cold launch — before WKWebView has
  // hydrated cookies. Retry with escalating delays (800ms, then 1500ms) to
  // give the cookie store time to wake up.
  const RETRY_DELAYS = [800, 1500];
  let lastErr;
  try {
    return await _request(`${BASE_AUTH}?action=refresh`, opts);
  } catch (e) {
    if (e?.status !== 401) throw e;
    lastErr = e;
  }
  for (const delay of RETRY_DELAYS) {
    console.log(`[v2 api] /refresh 401 — retrying after ${delay}ms`);
    await new Promise(r => setTimeout(r, delay));
    try {
      return await _request(`${BASE_AUTH}?action=refresh`, opts);
    } catch (e) {
      if (e?.status !== 401) throw e;
      lastErr = e;
    }
  }
  throw lastErr;
}

export async function logout() {
  return _request(`${BASE_AUTH}?action=logout`, {
    method: 'POST',
    headers: _authHeaders(),
  });
}

export async function fetchRealtimeConfig() {
  // Authenticated since #81 — server returns a per-account private
  // broadcast topic that callers MUST use verbatim instead of computing
  // their own. Without the JWT this 401s, by design (cookie fallback
  // covers the boot rehydrate case post-#108).
  return _request(`${BASE_AUTH}?action=realtime-config`, {
    headers: _authHeaders(),
  });
}

// ── Proxy: permissions check (#165) ─────────────────────────────────────────

/**
 * Ask the proxy whether the current session has the CRM permissions the
 * inbox needs. Returns `{ success, data: { ok, ownerBypass, missing_required,
 * runtime_hints, missing_recommended } }`. Bypasses the hard gate so it
 * works even for sessions missing `crm.live_chat.ALL`.
 */
export async function fetchPermissionsCheck() {
  return _proxyGet(new URLSearchParams({ path: '/permissions-check' }));
}

// ── Proxy: lead notes (#167) ────────────────────────────────────────────────
//
// Per-(contact, channel) timeline. The proxy mediates all writes so RLS on
// inbox_lead_notes can stay service-key-only. Auto-rows for transfer /
// assigned / done are written server-side by the /chats/:id/assign and
// /chats/:id/done handlers — clients never set kind explicitly here.

export async function fetchLeadNotes(contactId, channelPhoneId) {
  const qs = new URLSearchParams({
    path:             '/lead-notes',
    contact_id:       contactId,
    channel_phone_id: channelPhoneId,
  });
  return _proxyGet(qs);
}

export async function createLeadNote({ contactId, channelPhoneId, body }) {
  return _proxyPost('/lead-notes', { contact_id: contactId, channel_phone_id: channelPhoneId, body });
}

export async function deleteLeadNote(id) {
  // Use a hand-rolled DELETE since _proxyDelete doesn't exist (the only
  // other DELETE-via-body call is the tag remove route, which has its own
  // helper). Sends an empty JSON body so the proxy's parser doesn't choke.
  const qs = new URLSearchParams({ path: `/lead-notes/${id}` });
  return _request(`${BASE_PROXY}?${qs}`, {
    method: 'DELETE',
    headers: { ...(_authHeaders()), 'Content-Type': 'application/json' },
    body: '{}',
  });
}

// ── Proxy: chats ────────────────────────────────────────────────────────────

export async function fetchChats({ page = 1, limit = 20, agent, status } = {}) {
  const qs = new URLSearchParams({ path: '/chats', page: String(page), limit: String(limit) });
  if (agent)  qs.set('agent', agent);
  if (status) qs.set('status', status);
  return _proxyGet(qs);
}

export async function searchChats(query, field = 'name') {
  const qs = new URLSearchParams({ path: '/chats/search', q: query, field });
  return _proxyGet(qs);
}

export async function fetchMessages(chatId, phone, { page = 1, limit = 20, sort = 'newest', channelPhoneId = null } = {}) {
  const qs = new URLSearchParams({
    path: `/chats/${chatId}/messages`,
    page: String(page),
    limit: String(limit),
    sort,
    phone,
  });
  // #153: scope the inbox_messages query to a single channel when the thread
  // is opened for a specific per-channel row. Without this filter, opening
  // Sambit's Prinstaindia row would show messages from the ODIV channel too.
  if (channelPhoneId) qs.set('channel_phone_id', channelPhoneId);
  return _proxyGet(qs);
}

export async function sendMessage(body) {
  return _proxyPost('/messages', body);
}

/**
 * Mark a batch of inbound message wamids as read on the WA platform so the
 * customer sees the blue ticks. Caller groups by channel and supplies the
 * matching phone_number_id.
 */
export async function markMessagesRead(wamids, channelPhoneId) {
  return _proxyPost('/messages/mark-read', {
    wamids,
    _phone_number_id: channelPhoneId,
  });
}

/**
 * #158 — bulk DB-level mark-read for an entire (contact, channel) pair.
 * Clears the status='delivered' flag on every inbound row in the history,
 * not just the ones currently loaded in the thread window. Separate from
 * the per-wamid markMessagesRead above because this one does NOT touch
 * Meta's Cloud API (old messages are past the read-receipt window).
 */
export async function markChannelRead(phone, channelPhoneId) {
  return _proxyPost('/messages/mark-channel-read', {
    phone,
    channel_phone_id: channelPhoneId,
  });
}

export async function uploadMedia(body) {
  return _proxyPost('/media/upload', body);
}

// fetchTemplates removed in #100 — agent inbox no longer offers template
// re-engagement. Outbound campaigns belong to marketing surfaces.
// The proxy /templates route is preserved server-side for any future
// admin/analytics consumers.

/**
 * Assign a chat to a team member.
 *
 * #163: when `channelPhoneId` is supplied, the assignment is scoped
 * to that single (contact, channel) row via the shadow table. When
 * omitted, the proxy falls back to contact-scoped legacy behavior.
 */
export async function assignChat(chatId, phone, email, channelPhoneId = null, opts = {}) {
  const body = { email };
  if (channelPhoneId) body.channel_phone_id = channelPhoneId;

  // #167 — when the user types a hand-off note in the transfer confirm
  // modal, ship it inline so the proxy can stamp the lead-notes auto-row
  // with `body=handoffNote`. Empty/whitespace-only is treated as absent.
  // Support both the old positional handoffNote arg and the new opts bag
  // for backwards compat with any straggler callsites.
  const handoffNote = typeof opts === 'string' ? opts : (opts.handoffNote || null);
  if (handoffNote && String(handoffNote).trim()) {
    body.handoff_note = String(handoffNote).trim().slice(0, 500);
  }

  // Fix for "Assigned to hello" + missing transfer notes:
  //   - agent_name → server uses it for the system-message divider label
  //                  ("Assigned to Sambit Swarup" vs "Assigned to hello")
  //   - previous_agent_email → server uses it to decide kind='transfer'
  //                            and stamp transfer_from_email when known.
  //                            Thread.js has live.agent so it always knows.
  if (typeof opts === 'object' && opts) {
    if (opts.agentName)           body.agent_name           = String(opts.agentName).slice(0, 80);
    if (opts.previousAgentEmail)  body.previous_agent_email = String(opts.previousAgentEmail).toLowerCase();
    // #172 — customer_name reaches the receiving agent's notification
    // ("Sambit assigned you Customer X"). Thread.js has chat.name in
    // scope so the client-side cost is zero.
    if (opts.customerName)        body.customer_name        = String(opts.customerName).slice(0, 80);
  }
  return _proxyPost(`/chats/${chatId}/assign`, body, { phone });
}

/**
 * Mark a chat as done. Same per-row semantics as assignChat — pass
 * `channelPhoneId` to mark only that row, omit for contact-scoped.
 */
export async function markDone(chatId, phone, channelPhoneId = null) {
  const body = channelPhoneId ? { channel_phone_id: channelPhoneId } : {};
  return _proxyPost(`/chats/${chatId}/done`, body, { phone });
}

export async function closeChat(chatId, phone) {
  return _proxyPost(`/chats/${chatId}/close`, {}, { phone });
}

export async function fetchTeam() {
  return _proxyGet(new URLSearchParams({ path: '/team' }));
}

/**
 * #159 — fetch our own team directory (inbox_agents + owner) so the
 * client can resolve chat.agent ObjectIds to human names without
 * hitting the CRM. Backs services/team-directory.js.
 */
export async function fetchTeamDirectory() {
  return _proxyGet(new URLSearchParams({ path: '/team-directory' }));
}

export async function fetchTags(chatId) {
  return _proxyGet(new URLSearchParams({ path: `/chats/${chatId}/tags` }));
}

/**
 * Fetch the full tag catalog for the account — used to resolve the tag IDs
 * returned by /chats/{id}/tags into human-readable names + colors + icons.
 * Rarely changes so the UI can safely cache it for the session.
 */
export async function fetchAllTags() {
  return _proxyGet(new URLSearchParams({ path: '/tags' }));
}

/**
 * Add one or more tags to a contact by ID. Accepts an array of tag ObjectIds.
 * The CRM expects a comma-separated string in `tags` + type='by_id'.
 */
export async function addContactTags(chatId, tagIds) {
  const ids = Array.isArray(tagIds) ? tagIds : [tagIds];
  return _proxyPost(`/chats/${chatId}/tags`, {
    tags: ids.join(','),
    type: 'by_id',
  });
}

/**
 * Remove one or more tags from a contact by ID. Same comma-separated shape
 * as add, but via DELETE. The proxy forwards the method transparently so
 * we route through a shared helper here.
 */
export async function removeContactTags(chatId, tagIds) {
  const ids = Array.isArray(tagIds) ? tagIds : [tagIds];
  const qs = new URLSearchParams({ path: `/chats/${chatId}/tags` });
  return _request(`${BASE_PROXY}?${qs}`, {
    method: 'DELETE',
    headers: {
      ..._authHeaders(),
      'Content-Type':  'application/json',
    },
    body: JSON.stringify({ tags: ids.join(','), type: 'by_id' }),
  });
}

export async function fetchCustomFields(chatId) {
  return _proxyGet(new URLSearchParams({ path: `/chats/${chatId}/custom-fields` }));
}

/**
 * Fetch the global custom-field catalog for the account. Returns the list of
 * field definitions (name, type, description, _id). UI caches per session.
 */
export async function fetchAllCustomFields() {
  return _proxyGet(new URLSearchParams({ path: '/custom-fields' }));
}

// ── Quick Replies ───────────────────────────────────────────────────────────

/** Fetch the account's shared quick replies library. */
export async function fetchQuickReplies() {
  return _proxyGet(new URLSearchParams({ path: '/quick-replies' }));
}

/**
 * Create a new quick reply. Server assigns the id and returns the
 * persisted row.
 *
 * #170 — accepts optional `scope`:
 *   - 'account' → owner only, visible to every agent in the account
 *   - 'agent'   → personal to the caller (server pins agent_email)
 * Defaults to 'agent' if omitted (the safer assumption for client UIs).
 */
export async function createQuickReply({ shortcut, text, scope = 'agent' }) {
  return _proxyPost('/quick-replies', { shortcut, text, scope });
}

// ── Account settings (#170) ────────────────────────────────────────────────

export async function fetchAccountSettings() {
  return _proxyGet(new URLSearchParams({ path: '/account-settings' }));
}

export async function updateAccountSettings(patch) {
  // PATCH via proxy — owner-only on the server side.
  const qs = new URLSearchParams({ path: '/account-settings' });
  return _request(`${BASE_PROXY}?${qs}`, {
    method: 'PATCH',
    headers: { ...(_authHeaders()), 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
}

/**
 * Update an existing quick reply by id. Scoped server-side to the
 * caller's account — a PATCH against another account's row returns 404.
 * Pass only the fields you want to change; omitted fields are left alone.
 */
export async function updateQuickReply(id, { shortcut, text }) {
  const qs = new URLSearchParams({ path: `/quick-replies/${id}` });
  const patch = {};
  if (shortcut !== undefined) patch.shortcut = shortcut;
  if (text     !== undefined) patch.text     = text;
  return _request(`${BASE_PROXY}?${qs}`, {
    method: 'PATCH',
    headers: {
      ..._authHeaders(),
      'Content-Type':  'application/json',
    },
    body: JSON.stringify(patch),
  });
}

/** Soft-delete a quick reply by id. */
export async function deleteQuickReply(id) {
  const qs = new URLSearchParams({ path: `/quick-replies/${id}` });
  return _request(`${BASE_PROXY}?${qs}`, {
    method: 'DELETE',
    headers: _authHeaders(),
  });
}

// ── Channel meta (READ-ONLY in PWA, writes happen in end-user dashboard) ───

/**
 * Fetch account-wide channel meta (color override + Channel Label) per
 * task #98. Returns `{success, data: [{phone_number_id, color_hex, label,
 * updated_by, updated_at}]}`. PWA never writes — edits happen in the
 * end-user dashboard via dashboard-account.js with the customer JWT.
 */
export async function fetchChannelMeta() {
  return _proxyGet(new URLSearchParams({ path: '/channel-meta' }));
}

/**
 * Upsert custom-field values on a contact. Accepts an array of
 * { id, value } pairs — Meta's CRM upserts in one round trip.
 */
export async function updateContactCustomFields(chatId, fields) {
  const data = Array.isArray(fields) ? fields : [fields];
  return _proxyPost(`/chats/${chatId}/custom-fields`, { data });
}

/**
 * Remove a single custom field value from a contact by field ID.
 */
export async function removeContactCustomField(chatId, fieldId) {
  const qs = new URLSearchParams({ path: `/chats/${chatId}/custom-fields/${fieldId}` });
  return _request(`${BASE_PROXY}?${qs}`, {
    method: 'DELETE',
    headers: _authHeaders(),
  });
}

// ── Internal helpers ────────────────────────────────────────────────────────

async function _proxyGet(qs) {
  return _request(`${BASE_PROXY}?${qs}`, {
    headers: _authHeaders(),
  });
}

async function _proxyPost(path, body, extraParams = {}) {
  const qs = new URLSearchParams({ path, ...extraParams });
  return _request(`${BASE_PROXY}?${qs}`, {
    method: 'POST',
    headers: {
      ..._authHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
}

async function _proxyDelete(path, extraParams = {}) {
  const qs = new URLSearchParams({ path, ...extraParams });
  return _request(`${BASE_PROXY}?${qs}`, {
    method: 'DELETE',
    headers: _authHeaders(),
  });
}

// ── Scheduled messages ────────────────────────────────────────────────────

export async function createScheduledMessage(data) {
  return _proxyPost('/scheduled', data);
}

export async function getScheduledMessages(chatId, channelId) {
  const params = { path: '/scheduled', chat_id: chatId };
  if (channelId) params.channel_id = channelId;
  return _proxyGet(new URLSearchParams(params));
}

export async function cancelScheduledMessage(id) {
  return _proxyDelete(`/scheduled/${id}`);
}

export async function cancelAllScheduledMessages(chatId) {
  return _proxyDelete('/scheduled', { chat_id: chatId });
}

export { ApiError };
